import { useEffect, useRef, type ReactNode, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { Icone } from './Icone';

/**
 * A folha que sobe (onda 308, spec 06 §4).
 *
 * ── Para que serve ─────────────────────────────────────────────────
 *
 * Duas coisas do celular precisam de mais espaço do que a tela deixa e menos
 * do que uma tela inteira: o «Mais» da barra de baixo (a troca de espaço, os
 * módulos que não couberam e a conta) e os filtros das listas (309). Em vez
 * de duas soluções, uma só: uma folha presa ao rodapé, com o véu atrás, que
 * sobe ao toque e desce por qualquer um dos quatro caminhos que uma mão
 * espera — o ×, o véu, a tecla Esc e o arrasto da alça para baixo.
 *
 * ── O que ela garante ──────────────────────────────────────────────
 *
 *  · É um diálogo de verdade (`role="dialog"`, `aria-modal`): o leitor de
 *    tela anuncia o título e o Tab não escapa para a página de trás.
 *  · O foco vai para o primeiro item do corpo ao abrir (ou para o × se o
 *    corpo não tem nenhum) e VOLTA para o botão que abriu ao fechar —
 *    sem isso, quem usa teclado perde o lugar.
 *  · Enquanto aberta, a página de trás não rola.
 *  · Sobe no `document.body`, por portal: nenhuma regra de `overflow` ou
 *    `transform` de um ancestral a prende ou a corta.
 *  · Fechada, não desenha nada (e quem a usa não paga o corpo à toa).
 *
 * O desenho (camadas, alça, safe-area) está no `celular.css`, fora do
 * `@media`: a folha só é aberta no celular, mas o desenho não depende da
 * largura.
 */

/** O que o Tab alcança. */
const FOCAVEL = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), '
  + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Os focáveis VISÍVEIS: um item escondido pelo CSS (`display: none`) não
 *  recebe foco, e mirar nele deixaria o foco fora da folha. */
const focaveis = (raiz: HTMLElement | null) => (raiz
  ? [...raiz.querySelectorAll<HTMLElement>(FOCAVEL)].filter((el) => el.getClientRects().length > 0)
  : []);

/** Quanto o dedo precisa descer na alça para a folha fechar. */
const ARRASTO_QUE_FECHA = 60;

export function FolhaDeBaixo({
  aberta, titulo, aoFechar, children, id, classe, rodape,
}: {
  aberta: boolean;
  /** O nome do diálogo — vai no cabeçalho e no `aria-label`. */
  titulo: string;
  aoFechar(): void;
  children: ReactNode;
  id?: string;
  /** Classe extra, para o estilo de quem usa (`folha-filtros`, `folha-mais`…). */
  classe?: string;
  /** Faixa fixa embaixo do corpo (os botões «Limpar» e «Ver»). */
  rodape?: ReactNode;
}) {
  const folha = useRef<HTMLElement>(null);
  const corpo = useRef<HTMLDivElement>(null);
  const botaoFechar = useRef<HTMLButtonElement>(null);
  /* Onde o dedo pousou na alça; `null` = nenhum arrasto em curso. */
  const arrasto = useRef<number | null>(null);
  /* O efeito abaixo depende só de `aberta`; quem fecha pode mudar a cada render. */
  const fechar = useRef(aoFechar);
  fechar.current = aoFechar;

  useEffect(() => {
    if (!aberta) return undefined;

    const anterior = document.activeElement as HTMLElement | null;
    const primeiro = focaveis(corpo.current)[0] ?? botaoFechar.current;
    primeiro?.focus({ preventScroll: true });

    const aoTeclar = (e: KeyboardEvent) => {
      /* Uma janela aberta por cima da folha (o `<dialog>` do `Dialogo`, que
         sobe na camada do topo) cuida das próprias teclas: o Esc dela fecha
         só ela, e o Tab dela não é puxado para a folha. */
      if (e.target instanceof Element && e.target.closest('dialog')) return;
      if (e.key === 'Escape') {
        fechar.current();
        return;
      }
      if (e.key !== 'Tab' || !folha.current) return;
      /* O Tab dá a volta dentro da folha. */
      const itens = focaveis(folha.current);
      if (itens.length === 0) return;
      const ultimo = itens[itens.length - 1];
      const foco = document.activeElement;
      if (!folha.current.contains(foco)) {
        e.preventDefault();
        itens[0].focus();
      } else if (e.shiftKey && foco === itens[0]) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && foco === ultimo) {
        e.preventDefault();
        itens[0].focus();
      }
    };
    document.addEventListener('keydown', aoTeclar);

    const rolagem = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = rolagem;
      if (anterior && document.contains(anterior)) anterior.focus({ preventScroll: true });
    };
  }, [aberta]);

  if (!aberta) return null;

  const aoTocar = (e: TouchEvent) => { arrasto.current = e.touches[0]?.clientY ?? null; };
  const aoMover = (e: TouchEvent) => {
    const inicio = arrasto.current;
    const y = e.touches[0]?.clientY;
    if (inicio !== null && y !== undefined && y - inicio > ARRASTO_QUE_FECHA) {
      arrasto.current = null;
      aoFechar();
    }
  };
  const aoSoltar = () => { arrasto.current = null; };

  return createPortal(
    <>
      <div className="folha-veu" onClick={aoFechar} aria-hidden="true" />
      <section
        ref={folha}
        id={id}
        className={classe ? `folha-de-baixo ${classe}` : 'folha-de-baixo'}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
      >
        <div
          className="folha-alca"
          onTouchStart={aoTocar}
          onTouchMove={aoMover}
          onTouchEnd={aoSoltar}
          onTouchCancel={aoSoltar}
        />
        <header className="folha-cabeca">
          <h2>{titulo}</h2>
          <button ref={botaoFechar} type="button" className="folha-fechar" aria-label="Fechar" onClick={aoFechar}>
            <Icone nome="fechar" />
          </button>
        </header>
        <div className="folha-corpo" ref={corpo}>{children}</div>
        {rodape && <footer className="folha-rodape">{rodape}</footer>}
      </section>
    </>,
    document.body,
  );
}
